/* seguroManual.js — seguro capturado a mano por un admin cuando RODAC no
 * cotiza el auto ("Este auto requiere cotización con asesor").
 *
 * Contrato con el backend: backend/docs/plans/admin-recotizar-placa-seguro-manual.md §4.1
 * (App\Support\Cotizaciones\SeguroManual). Lo usan la calculadora de la landing
 * (useHeroQuote) y el diálogo de recotizar de admin/usuarios.
 *
 * - Se piden exactamente los 3 plazos que el motor guarda (plazosDelSeguro).
 * - La prima es la TOTAL del plazo con IVA: la misma magnitud que manda RODAC
 *   (`PrimaTotal`), no la anual.
 * - Cada plazo es su propia fila de seguro y lleva SU folio (`folios[plazo]`):
 *   la aseguradora da uno por plazo, como RODAC da un ID por plazo. Es
 *   obligatorio y no se repite entre plazos; si no lo tienen, "No lo tengo"
 *   genera uno (generarFolio) y lo marca en `foliosAuto[plazo]`.
 * - El front manda `insurance_id: null`; el id lo pone el backend.
 */

export const PLAZOS = [24, 36, 48, 60];
export const PRIMA_MAXIMA = 2000000;
export const MAX_ASEGURADORA = 60;
export const MAX_FOLIO = 60;

const limpiar = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

/* "No lo tengo": referencia de Peicar cuando la aseguradora no dio folio.
 * Sin 0/O ni 1/I para que se pueda dictar por teléfono. */
const ALFABETO_FOLIO = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function generarFolio() {
  const bytes = new Uint8Array(8);
  const cripto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cripto?.getRandomValues) cripto.getRandomValues(bytes);
  else bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
  return `PEI-${Array.from(bytes, (b) => ALFABETO_FOLIO[b % ALFABETO_FOLIO.length]).join('')}`;
}

/* Los 3 plazos que el motor guarda para el plazo elegido: el elegido y sus
 * vecinos. Espejo de BudgetController::cotizador (financiero) y
 * CalculosCotizador::filterAndModifyInsurances (puro).
 * 24 → [24,36,48] · 36 → [24,36,48] · 48 → [36,48,60] · 60 → [36,48,60] */
export function plazosDelSeguro(plazo, plazos = PLAZOS) {
  const lista = [...new Set(plazos.map(Number))].sort((a, b) => a - b);
  const i = lista.indexOf(Number(plazo));
  if (i === -1) return [];
  if (lista.length <= 3) return lista;
  const offset = Math.max(0, Math.min(i - 1, lista.length - 3));
  return lista.slice(offset, offset + 3);
}

/* "≈ $X/mes" junto a cada prima, igual que el selector de seguros. */
export const primaMensual = (prima, plazo) => {
  const p = Number(prima);
  const n = Number(plazo);
  return p > 0 && n > 0 ? p / n : 0;
};

/* Valida lo capturado. `errores.montos` y `errores.folios` van por plazo:
 * { montos: { 36: 'Captura la prima de 36 meses.' }, folios: {} }. */
export function validarSeguroManual({ plazo, company, montos = {}, folios = {} } = {}) {
  const errores = { montos: {}, folios: {} };
  const requeridos = plazosDelSeguro(plazo);
  if (!requeridos.length) errores.plazo = 'Elige un plazo válido.';

  const aseguradora = limpiar(company);
  const vistos = {}; // folio en mayúsculas → plazo donde apareció primero
  if (!aseguradora) errores.company = 'Elige la aseguradora.';
  else if (aseguradora.length > MAX_ASEGURADORA) errores.company = `Máximo ${MAX_ASEGURADORA} caracteres.`;

  requeridos.forEach((p) => {
    const valor = montos?.[p];
    const n = Number(valor);
    if (valor === '' || valor === null || valor === undefined || Number.isNaN(n)) {
      errores.montos[p] = `Captura la prima de ${p} meses.`;
    } else if (n <= 0) {
      errores.montos[p] = 'La prima debe ser mayor a $0.';
    } else if (n > PRIMA_MAXIMA) {
      errores.montos[p] = 'La prima no puede pasar de $2,000,000.';
    }
    const folio = limpiar(folios?.[p]);
    const llave = folio.toUpperCase();
    if (!folio) errores.folios[p] = 'Captura el folio o usa «No lo tengo».';
    else if (folio.length > MAX_FOLIO) errores.folios[p] = `Máximo ${MAX_FOLIO} caracteres.`;
    else if (vistos[llave]) errores.folios[p] = `Se repite con el de ${vistos[llave]} meses.`;
    else vistos[llave] = p;
  });

  const ok = !errores.plazo && !errores.company
    && Object.keys(errores.montos).length === 0 && Object.keys(errores.folios).length === 0;
  return { ok, errores };
}

/* Campos del payload del cotizador con el seguro manual aplicado (§4.1).
 * Se mezclan ENCIMA del payload normal: reemplazan insurances/insurance/insurance_id.
 * Las llaves numéricas de `insurances` salen en orden ascendente en JSON: el
 * motor puro corta por índice. */
export function payloadSeguroManual({ plazo, company, montos = {}, folios = {}, foliosAuto = {} } = {}) {
  const aseguradora = limpiar(company);
  const insurances = {};
  plazosDelSeguro(plazo).forEach((p) => {
    insurances[p] = {
      insurance: Number(montos[p]),
      insurance_id: null,
      company: aseguradora,
      manual: true,
      folio: limpiar(folios?.[p]) || null,
      folio_auto: Boolean(foliosAuto?.[p]),
    };
  });
  return {
    seguro_manual: true,
    insurance_id: null,
    insurance: Number(montos[plazo]),
    insurances,
  };
}

/* Firma estable de lo capturado, para el dedupe y la llave de cache de la landing. */
export function firmaSeguroManual(sm) {
  if (!sm) return null;
  return JSON.stringify([
    limpiar(sm.company),
    PLAZOS.map((p) => limpiar(sm.folios?.[p])),
    PLAZOS.map((p) => Boolean(sm.foliosAuto?.[p])),
    PLAZOS.map((p) => Number(sm.montos?.[p]) || 0),
  ]);
}

/* Lo que regresa el GET de recotización (`seguro_actual`) → estado del formulario. */
export function seguroManualDesdeActual(actual) {
  if (!actual || !actual.manual) return null;
  const montos = {};
  Object.entries(actual.montos || {}).forEach(([p, v]) => { montos[Number(p)] = Number(v); });
  const folios = {};
  Object.entries(actual.folios || {}).forEach(([p, v]) => { if (v) folios[Number(p)] = String(v); });
  const foliosAuto = {};
  Object.entries(actual.folios_auto || {}).forEach(([p, v]) => { if (v) foliosAuto[Number(p)] = true; });
  return { company: actual.company || '', montos, folios, foliosAuto };
}
